import { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTitle } from 'react-use';

import { IOpenDataResponse } from 'ApiPackage/OpenDataModule/interfaces/IOpenDataResponse';
import { coreHideNotices } from 'CorePackage/MainModule/actions/coreActions';
import { Layout } from 'CorePackage/MainModule/components/Layout';
import { federalLawNameSelectOptions } from 'CorePackage/MainModule/constants/federalLawNameSelectOptions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { ISelectOption } from 'CorePackage/MainModule/interfaces/ISelectOption';
import { getCoreScreenSize } from 'CorePackage/MainModule/selectors/coreSelectors';
import { DownloadButton } from 'UIPackage/MainModule/components/DownloadButton';
import { SelectInput } from 'UIPackage/MainModule/components/SelectInput';

import { IActiveTab } from '../interfaces/IActiveTab';
import styles from './OpenData.module.scss';
import { publicOpenDataGetData, publicOpenDataSetActiveTab } from '../actions/publicOpenDataActions';
import { getOpenDataTitle } from '../common/getOpenDataTitle';
import { getPublicOpenDataFetchedData, getPublicOpenDataActiveTab } from '../reducers/publicOpenDataReducer';

interface IOpenDataStoreProps {
  fetchedData?: IOpenDataResponse;
  activeTab?: IActiveTab;
}

const OpenDataWithoutStore = ({ fetchedData, activeTab }: IOpenDataStoreProps) => {
  useTitle('Открытые данные');
  const dispatch = useDispatch();

  useEffect(() => {
    window.scroll(0, 0);
    const firstOption = federalLawNameSelectOptions.find(item => !item.value);
    if (firstOption) {
      dispatch(publicOpenDataGetData(firstOption));
    }
    return () => {
      dispatch(coreHideNotices());
    };
  }, [dispatch]);

  const onChangeFederalLaw = (federalLaw: ISelectOption<string> & { value: boolean }) =>
    dispatch(publicOpenDataGetData(federalLaw));

  const onChangeActiveTab = (year: { id: string; label: string; value: string }) =>
    activeTab && dispatch(publicOpenDataSetActiveTab({ ...activeTab, year }));

  return (
    <Layout>
      {fetchedData && (
        <div className={styles.CustomSize}>
          <h2 className={styles.SectionTitle}>Открытые данные</h2>
          <div className={styles.Container}>
            <div className={styles.RegistryContainer}>
              <h4 className={styles.Title}>Реестр наборов данных</h4>
              <div className={styles.DownloadLinkWrap}>
                <DownloadButton className={styles.DownloadLink} url={fetchedData.registryXmlUrl}>
                  Скачать
                </DownloadButton>
              </div>
            </div>
            <div className={styles.TabsContainer}>
              <h4 className={styles.Title}>Наборы данных</h4>
              <div className={styles.SelectFieldContainer}>
                <SelectInput
                  value={activeTab ? activeTab.year : undefined}
                  onChange={onChangeActiveTab}
                  className={styles.SelectField}
                  options={fetchedData.years
                    .sort((a, b) => b - a)
                    .map(year => ({ label: year.toString(), id: year, value: year }))}
                />
                <SelectInput
                  value={activeTab ? activeTab.federalLaw : undefined}
                  onChange={onChangeFederalLaw}
                  id="federalLaw"
                  className={styles.FederalLawSelectField}
                  options={federalLawNameSelectOptions}
                />
              </div>
              {activeTab && (
                <div>
                  {Object.keys(fetchedData.months)
                    .reverse()
                    .filter(item => Number(item) === Number(activeTab.year.value))
                    .map(year => {
                      const yearNumber = +year;
                      const yearMonths = fetchedData.months[yearNumber];
                      return (
                        <div key={yearNumber}>
                          {yearMonths.map(monthId => {
                            return (
                              <div className={styles.LinkWrap} key={monthId}>
                                <Link
                                  className={styles.Link}
                                  to={`/portal/public-open-data/check/${year}/${monthId}?isFederalLaw248=${activeTab.federalLaw.value}`}
                                >
                                  {getOpenDataTitle(yearNumber, monthId)}
                                </Link>
                              </div>
                            );
                          })}
                          <div className={styles.LinkWrap}>
                            <Link
                              className={styles.Link}
                              to={`/portal/public-open-data/check/${year}/plans?isFederalLaw248=${activeTab.federalLaw.value}`}
                            >
                              {getOpenDataTitle(yearNumber)}
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
          <Information />
        </div>
      )}
    </Layout>
  );
};

const Information = () => {
  return (
    <div className={styles.Information}>
      <h3>Условия использования набора данных</h3>
      <p>
        Пользователь без заключения договора с Федеральной службой государственной статистики может использовать (в том
        числе повторно) открытые данные свободно, бесплатно, бессрочно, объединять их с другой информацией, использовать
        открытые данные в некоммерческих целях, использовать для создания программ для ЭВМ и приложении.
      </p>
      <p>При использовании открытых данных Пользователь обязан:</p>
      <ul>
        <li>Использовать открытые данные только в законных целях;</li>
        <li>Не искажать открытые данные при их использовании;</li>
        <li>Сохранять ссылку на источник информации при использовании открытых данных</li>
      </ul>
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  screenSize: getCoreScreenSize(state),
  fetchedData: getPublicOpenDataFetchedData(state),
  activeTab: getPublicOpenDataActiveTab(state),
});

export const OpenData = connect(mapStateToProps)(OpenDataWithoutStore);
